import { useApolloClient, useLazyQuery } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useHistory } from "react-router";
import * as yup from "yup";
import useDeleteCredential from "../../graphql/hooks/mutations/useDeleteCredential";
import useUserLogout from "../../graphql/hooks/mutations/useUserLogout";
import { GET_VIEWER } from "../../graphql/hooks/queries/useGetViewer";
import useAddNotification from "../../hooks/useAddNotification";
import useTranslations from "../../hooks/useTranslations";
import useUpdateViewer from "../../hooks/useUpdateViewer";
import { Button, Input, Radio, RadioGroup } from "../wombat-ui";
import { H3 } from "../wombat-ui/components/Typography";

type ViewerInput = {
  id?: string;
  name: string;
  email: string;
  locale: string;
  avatar: {
    filename: string;
    data: string;
  };
};

type UserFormProps = {
  defaultValues?: ViewerInput;
};

const LOCALE_OPTIONS = [
  { value: "fr-FR", label: "Francais" },
  { value: "en-US", label: "English" },
];

const UserForm: React.FC<UserFormProps> = ({ defaultValues = {} }) => {
  const fileRef = useRef<{ filename: string; data: string }>(null);
  const t = useTranslations();
  const history = useHistory();

  const [updateViewer] = useUpdateViewer();
  const [addNotification] = useAddNotification();

  const ViewerFormSchema = useMemo(
    () =>
      yup.object().shape({
        name: yup.string().required(t("forms.errors.required") as string),
        email: yup
          .string()
          .email(t("forms.errors.email") as string)
          .required(t("forms.errors.required") as string),
      }),
    [t]
  );

  const {
    register,
    setValue,
    handleSubmit,
    errors,
    control,
  } = useForm<ViewerInput>({
    resolver: yupResolver(ViewerFormSchema),
    defaultValues: {
      name: "",
      email: "",
      avatar: { filename: "", data: "" },
      ...defaultValues,
    },
  });

  const [deleteCredential] = useDeleteCredential();
  const [userLogout] = useUserLogout();
  const apolloClient = useApolloClient();
  const handleLogout = useCallback(async () => {
    if (confirm(t("viewerProfile.logoutConfirmation"))) {
      await userLogout();
      await deleteCredential();
      apolloClient.resetStore();
    }
  }, [deleteCredential, userLogout, apolloClient]);

  const [getViewer, { error: getViewerError }] = useLazyQuery(GET_VIEWER, {
    fetchPolicy: "network-only",
  });
  useEffect(() => {
    if (getViewerError) {
      deleteCredential();
      apolloClient.resetStore();
    }
  }, [getViewerError, deleteCredential, apolloClient]);

  const onSubmit = useCallback(
    async ({ name, email, avatar, locale }) => {
      try {
        await updateViewer({
          variables: {
            input: {
              name,
              email,
              locale,
              avatar: avatar.filename !== "" ? avatar.data : null,
            },
          },
        });

        addNotification({
          variables: {
            title: t(`userProfile.notification.success`) as string,
            body: t(`userProfile.notification.profileUpdated`) as string,
            status: "success",
          },
        });
      } catch (error) {
        addNotification({
          variables: {
            title: t(`userProfile.notification.error`) as string,
            status: "error",
          },
        });
      }

      // We manually refetch the user instead of using refetchQueries so
      // we can easily know if the user was logged out. It can happen
      // when the password or email change.
      await getViewer();
    },
    [defaultValues, handleLogout]
  );

  useEffect(() => {
    register(`avatar.filename`);
    register(`avatar.data`);
  }, [errors]);

  useEffect(() => {
    register(`avatar.filename`);
    register(`avatar.data`);
    setValue(`avatar.filename`, fileRef.current?.filename);
    setValue(`avatar.data`, fileRef.current?.data);
  }, []);

  const avatarData = useWatch({
    control: control,
    name: `avatar.data`,
    defaultValue: "",
  });

  const handleAvatarChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const reader = new FileReader();
      const file = e.target?.files?.[0];
      reader.readAsDataURL(file);

      reader.onload = () => {
        setValue(`avatar.filename`, file.name);
        setValue(`avatar.data`, reader.result);
        fileRef.current = {
          filename: file.name,
          data: reader.result as string,
        };
      };
    },
    []
  );

  return (
    <form
      className="relative block"
      id="userForm"
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className="flex flex-col">
        <div>
          <H3>{t("viewerProfile.myInformation")}</H3>
          <div>
            <label className="block pt-4 text-sm font-medium text-dark-100">
              {t("viewerProfile.avatar")}
            </label>
            <div className="flex items-center mt-2">
              {avatarData !== "" || defaultValues?.avatar?.data ? (
                <img
                  className="object-cover w-12 h-12 rounded-full"
                  src={
                    avatarData !== "" ? avatarData : defaultValues?.avatar?.data
                  }
                  alt={`User-avatar`}
                />
              ) : (
                <span className="w-12 h-12 overflow-hidden bg-gray-100 rounded-full">
                  <svg
                    className="w-full h-full text-gray-300"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </span>
              )}
              <span className="relative ml-5 overflow-hidden rounded-md shadow-sm">
                <button
                  type="button"
                  className="px-3 py-2 text-sm font-medium leading-4 text-gray-700 transition duration-150 ease-in-out border border-gray-300 rounded-md hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-50 active:text-gray-800"
                >
                  {t("forms.actions.change")}
                </button>
                <input
                  className="absolute top-0 right-0 block py-2 opacity-0 cursor-pointer"
                  type="file"
                  onChange={handleAvatarChange}
                  accept="image/png, image/jpeg, image/jpg"
                  name="avatar"
                />
              </span>
            </div>
          </div>
          <div className="grid grid-cols-1 col-span-4 mt-6 gap-y-6 gap-x-4 sm:grid-cols-6">
            <div className="sm:col-span-3">
              <Input
                id="user-name"
                label={t("viewerProfile.name") as string}
                error={errors?.name}
                name={`name`}
                ref={register({ required: true })}
              />
            </div>
            <div className="sm:col-span-4">
              <Input
                id="user-email"
                label={t("viewerProfile.email") as string}
                error={errors?.email}
                name={`email`}
                ref={register({ required: true })}
              />
            </div>
          </div>
        </div>
        <H3>{t("viewerProfile.locale")}</H3>
        <RadioGroup>
          {LOCALE_OPTIONS.map(({ label, value }) => (
            <Radio
              key={value}
              id={`locale-${value}`}
              label={label}
              name="locale"
              value={value}
              ref={register}
            />
          ))}
        </RadioGroup>
        <H3>{t("viewerProfile.session")}</H3>
        <div className="pt-4">
          <Button onClick={handleLogout} variant="danger" id="logoutButton">
            {t("viewerProfile.logout")}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default React.memo(UserForm);
